import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import {
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
} from 'recharts';

function formatMetricLabel(type) {
  if (!type) return '';
  return String(type)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function formatDateOnly(d) {
  return d.toISOString().split('T')[0];
}

function ExplorerChart({ rows, xKey, yKey, yUnit, timeLabelMode = 'date' }) {
  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .filter((r) => {
        if (!r) return false;
        const xVal = r[xKey];
        const yVal = r[yKey];
        
        if (!Number.isFinite(xVal) || !Number.isFinite(yVal)) return false;
        
        const isWorkoutKey = (key) => key.startsWith('workout_') || key === 'workout_minutes' || key === 'workout_count' || key === 'workout_calories_kcal' || key === 'workout_avg_heart_rate_bpm';
        const isTimeseriesKey = (key) => key.startsWith('ts_');
        
        if (isWorkoutKey(xKey) && isTimeseriesKey(yKey) && xVal === 0) return false;
        if (isWorkoutKey(yKey) && isTimeseriesKey(xKey) && yVal === 0) return false;
        
        return true;
      })
      .slice()
      .sort((a, b) => Number(a[xKey]) - Number(b[xKey]));
  }, [rows, xKey, yKey]);

  if (sorted.length < 2) {
    return (
      <Box
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 12,
          padding: 40,
          background: 'var(--mantine-color-gray-0)',
          textAlign: 'center',
        }}
      >
        <Text size="sm" c="dimmed">
          Not enough data to plot this combination. Try a different axis or date range.
        </Text>
      </Box>
    );
  }

  const isTimeSeries = xKey === 'timestamp' || timeLabelMode === 'datetime';
  const ChartComponent = isTimeSeries ? LineChart : ScatterChart;
  const DataComponent = isTimeSeries ? Line : Scatter;

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ChartComponent
        data={sorted}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={xKey}
          type={isTimeSeries ? 'number' : 'number'}
          scale={isTimeSeries ? 'time' : 'linear'}
          domain={isTimeSeries ? ['dataMin', 'dataMax'] : ['dataMin', 'dataMax']}
          tickFormatter={
            isTimeSeries
              ? (v) => new Date(v).toLocaleDateString()
              : undefined
          }
        />
        <YAxis
          label={{ value: yUnit ? `(${yUnit})` : '', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip
          contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
          formatter={(value) => [typeof value === 'number' ? value.toFixed(2) : value, yKey]}
          labelFormatter={
            isTimeSeries
              ? (label) => new Date(label).toLocaleString()
              : (label) => String(label)
          }
        />
        {isTimeSeries && (
          <Brush
            dataKey={xKey}
            height={30}
            stroke="#8884d8"
            tickFormatter={(v) => new Date(v).toLocaleDateString()}
          />
        )}
        <DataComponent
          type="monotone"
          dataKey={yKey}
          stroke="#8884d8"
          fill="#8884d8"
          dot={!isTimeSeries}
        />
      </ChartComponent>
    </ResponsiveContainer>
  );
}

function FindCorrelations() {
  const { user } = useAuth();
  const selectedUserId = user?.id || '';

  const [error, setError] = useState('');

  const [rangePreset, setRangePreset] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [xAxis, setXAxis] = useState('timestamp');
  const [yAxis, setYAxis] = useState('');
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [metricUnit, setMetricUnit] = useState('');

  const [timeseries, setTimeseries] = useState(null);
  const [workouts, setWorkouts] = useState(null);
  const [sleep, setSleep] = useState(null);

  const range = useMemo(() => {
    const now = new Date();
    const today = formatDateOnly(now);
    
    if (rangePreset === 'custom' && customStart) {
      return {
        start: customStart,
        end: customEnd || today,
      };
    }
    const start = new Date(now);
    if (rangePreset === '7d') start.setDate(start.getDate() - 7);
    if (rangePreset === '30d') start.setDate(start.getDate() - 30);
    if (rangePreset === '90d') start.setDate(start.getDate() - 90);
    if (rangePreset === '365d') start.setDate(start.getDate() - 365);
    return { start: formatDateOnly(start), end: today };
  }, [rangePreset, customStart, customEnd]);

  const fetchData = async (startDate, endDate) => {
    setError('');
    try {
      const res = await apiService.wearables.getData(null, startDate, endDate, null);
      setTimeseries(res.data?.timeseries || null);
      setWorkouts(res.data?.workouts || null);
      setSleep(res.data?.sleep || null);

      const uniq = new Map();
      const items = res.data?.timeseries?.data || [];
      items.forEach((p) => {
        if (p && p.type) uniq.set(p.type, true);
      });
      const list = Array.from(uniq.keys()).sort();
      setAvailableMetrics(list);

      if (!yAxis && list.length > 0) {
        const preferred = ['heart_rate', 'recovery_score', 'resting_heart_rate', 'heart_rate_variability_rmssd', 'steps'];
        const nextMetric = preferred.find((m) => list.includes(m)) || list[0];
        setYAxis(`ts_${nextMetric}`);
      }

      const firstUnit = items.find((p) => p && p.unit)?.unit || '';
      setMetricUnit(firstUnit || '');

    } catch (e) {
      setError(`Failed to load data: ${e.response?.data?.detail || e.message}`);
    }
  };

  useEffect(() => {
    if (!selectedUserId) return;
    fetchData(range.start, range.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, range.start, range.end]);

  const dailyRows = useMemo(() => {
    const byDay = new Map();

    const ensureDay = (day) => {
      if (!day) return null;
      if (!byDay.has(day)) {
        byDay.set(day, {
          day,
          timestamp: new Date(`${day}T12:00:00Z`).getTime(),
          workout_minutes: 0,
          workout_count: 0,
          workout_calories_kcal: 0,
          workout_avg_heart_rate_bpm: null,
          sleep_hours: 0,
          sleep_score: null,
          _workout_hr_sum: 0,
          _workout_hr_count: 0,
          _sleep_score_sum: 0,
          _sleep_score_count: 0,
          _sleep_hours_sum: 0,
          _sleep_hours_count: 0,
          _ts: {},
        });
      }
      return byDay.get(day);
    };

    const ts = timeseries?.data || [];
    ts.forEach((p) => {
      const type = p?.type;
      const timestamp = p?.timestamp;
      const value = p?.value;
      if (!type || !timestamp || typeof value !== 'number') return;
      const day = String(timestamp).slice(0, 10);
      const row = ensureDay(day);
      if (!row) return;
      if (!row._ts[type]) row._ts[type] = { sum: 0, count: 0 };
      row._ts[type].sum += Number(value);
      row._ts[type].count += 1;
    });

    const ws = workouts?.data || [];
    ws.forEach((w) => {
      const start = w?.start_datetime || w?.start_time;
      if (!start) return;
      const day = String(start).slice(0, 10);
      const row = ensureDay(day);
      if (!row) return;

      row.workout_count += 1;
      row.workout_minutes += Number(w?.duration_seconds || 0) / 60;
      row.workout_calories_kcal += Number(w?.calories_kcal || 0);

      const avgHr = w?.avg_heart_rate_bpm;
      if (typeof avgHr === 'number' && !Number.isNaN(avgHr)) {
        row._workout_hr_sum += avgHr;
        row._workout_hr_count += 1;
      }
    });

    const ss = sleep?.data || [];
    ss.forEach((s) => {
      const start = s?.start_datetime || s?.start_time || s?.date;
      if (!start) return;
      const day = String(start).slice(0, 10);
      const row = ensureDay(day);
      if (!row) return;

      const durSec = Number(s?.duration_seconds || s?.total_sleep_time_seconds || 0);
      const hours = durSec > 0 ? durSec / 3600 : 0;
      if (hours > 0) {
        row._sleep_hours_sum += hours;
        row._sleep_hours_count += 1;
      }

      const score = s?.recovery_score ?? s?.sleep_score;
      if (typeof score === 'number' && !Number.isNaN(score)) {
        row._sleep_score_sum += score;
        row._sleep_score_count += 1;
      }
    });

    byDay.forEach((row) => {
      row.workout_minutes = Math.round(row.workout_minutes * 10) / 10;
      if (row._workout_hr_count > 0) {
        row.workout_avg_heart_rate_bpm = Math.round(row._workout_hr_sum / row._workout_hr_count);
      }
      if (row._sleep_hours_count > 0) {
        row.sleep_hours = Math.round((row._sleep_hours_sum / row._sleep_hours_count) * 10) / 10;
      }
      if (row._sleep_score_count > 0) {
        row.sleep_score = Math.round(row._sleep_score_sum / row._sleep_score_count);
      }
      Object.entries(row._ts).forEach(([type, agg]) => {
        const k = `ts_${type}`;
        row[k] = agg.count ? agg.sum / agg.count : null;
      });

      delete row._workout_hr_sum;
      delete row._workout_hr_count;
      delete row._sleep_score_sum;
      delete row._sleep_score_count;
      delete row._sleep_hours_sum;
      delete row._sleep_hours_count;
      delete row._ts;
    });

    return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [timeseries, workouts, sleep]);

  const axisCandidates = useMemo(() => {
    const base = [
      { key: 'timestamp', label: 'Date', unit: '' },
      { key: 'workout_minutes', label: 'Workout minutes (total)', unit: 'min' },
      { key: 'workout_count', label: 'Workout count', unit: '' },
      { key: 'workout_calories_kcal', label: 'Workout calories (total)', unit: 'kcal' },
      { key: 'workout_avg_heart_rate_bpm', label: 'Workout avg heart rate', unit: 'bpm' },
      { key: 'sleep_hours', label: 'Sleep hours', unit: 'hrs' },
      { key: 'sleep_score', label: 'Sleep/Recovery score', unit: '' },
    ];
    const ts = (availableMetrics || []).map((t) => ({
      key: `ts_${t}`,
      label: `Timeseries: ${formatMetricLabel(t)}`,
      unit: '',
    }));
    return [...base, ...ts];
  }, [availableMetrics]);

  const axisOptions = useMemo(() => {
    const hasAny = (k) => dailyRows.some((r) => Number.isFinite(r[k]));
    const x = axisCandidates.filter((c) => c.key === 'timestamp' || hasAny(c.key)).map((c) => ({ value: c.key, label: c.label }));
    const y = axisCandidates.filter((c) => c.key !== 'timestamp' && hasAny(c.key)).map((c) => ({ value: c.key, label: c.label }));
    return { x, y };
  }, [axisCandidates, dailyRows]);

  const yUnit = useMemo(() => {
    if (!yAxis) return '';
    if (yAxis.startsWith('ts_')) return metricUnit || '';
    const def = axisCandidates.find((c) => c.key === yAxis);
    return def?.unit || '';
  }, [axisCandidates, metricUnit, yAxis]);

  useEffect(() => {
    if (!yAxis || !yAxis.startsWith('ts_')) return;
    const yType = yAxis.slice(3);
    const items = timeseries?.data || [];
    const u = items.find((p) => p && p.type === yType && p.unit)?.unit || '';
    setMetricUnit(u);
  }, [yAxis, timeseries]);

  const rawTimeseriesRows = (() => {
    if (!yAxis || !yAxis.startsWith('ts_')) return [];
    if (xAxis !== 'timestamp') return [];
    const yType = yAxis.slice(3);
    const items = timeseries?.data || [];
    return items
      .filter((p) => p && p.type === yType && p.timestamp && typeof p.value === 'number')
      .map((p) => ({ timestamp: new Date(p.timestamp).getTime(), value: Number(p.value) }))
      .filter((r) => Number.isFinite(r.timestamp) && Number.isFinite(r.value))
      .sort((a, b) => a.timestamp - b.timestamp);
  })();

  const pairedTimeseriesRows = (() => {
    if (!xAxis.startsWith('ts_') || !yAxis || !yAxis.startsWith('ts_')) return [];
    
    const xType = xAxis.slice(3);
    const yType = yAxis.slice(3);
    const items = timeseries?.data || [];
    
    if (!Array.isArray(items) || items.length === 0) return [];
    
    const isSameType = xType === yType;
    
    const xPoints = [];
    const yPoints = [];
    
    items.forEach((p) => {
      if (!p || !p.timestamp) return;
      const value = p.value;
      if (value === null || value === undefined) return;
      if (typeof value !== 'number' && typeof value !== 'string') return;
      if (typeof value === 'string' && isNaN(parseFloat(value))) return;
      
      const ts = new Date(p.timestamp).getTime();
      if (!Number.isFinite(ts)) return;
      
      const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
      if (!Number.isFinite(numValue)) return;
      
      if (isSameType && p.type === xType) {
        xPoints.push({ timestamp: ts, value: numValue });
        yPoints.push({ timestamp: ts, value: numValue });
      } else {
        if (p.type === xType) {
          xPoints.push({ timestamp: ts, value: numValue });
        } else if (p.type === yType) {
          yPoints.push({ timestamp: ts, value: numValue });
        }
      }
    });
    
    if (xPoints.length === 0 || yPoints.length === 0) return [];
    
    if (isSameType) {
      return xPoints.map((xp) => ({ xValue: xp.value, yValue: xp.value })).sort((a, b) => a.xValue - b.xValue);
    }
    
    const matched = [];
    const yMap = new Map(yPoints.map(yp => [yp.timestamp, yp]));
    
    xPoints.forEach((xp) => {
      if (yMap.has(xp.timestamp)) {
        matched.push({ xValue: xp.value, yValue: yMap.get(xp.timestamp).value });
        return;
      }
      
      let closest = null;
      let minDiff = Infinity;
      yPoints.forEach((yp) => {
        const diff = Math.abs(xp.timestamp - yp.timestamp);
        if (diff <= 60000 && diff < minDiff) {
          minDiff = diff;
          closest = yp;
        }
      });
      
      if (closest) {
        matched.push({ xValue: xp.value, yValue: closest.value });
      }
    });
    
    return matched.sort((a, b) => a.xValue - b.xValue);
  })();

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'lg' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        <div>
          <Title order={1} size="h2" style={{ marginTop: 0 }}>
            Find Correlations
          </Title>
          <Text c="dimmed" mt={6}>
            Explore relationships between your health metrics, workouts, and sleep data.
          </Text>
        </div>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        <Card withBorder radius="lg" p="lg">
          <Stack gap="md">
            <div>
              <Text fw={650}>Explore Correlations</Text>
              <Text size="sm" c="dimmed" mt={4}>
                Choose a date range and X/Y axes to explore relationships between timeseries, workouts, and sleep.
              </Text>
            </div>

            <Grid gutter="md" align="end">
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Select
                  label="Range"
                  value={rangePreset}
                  onChange={(v) => setRangePreset(v || '30d')}
                  data={[
                    { value: '7d', label: 'Last 7 days' },
                    { value: '30d', label: 'Last 30 days' },
                    { value: '90d', label: 'Last 90 days' },
                    { value: '365d', label: 'Last 12 months' },
                    { value: 'custom', label: 'Custom' },
                  ]}
                />
              </Grid.Col>

              {rangePreset === 'custom' && (
                <>
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <TextInput
                      label="Start"
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <TextInput
                      label="End"
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                    />
                  </Grid.Col>
                </>
              )}

              <Grid.Col span={{ base: 12, sm: 6, md: rangePreset === 'custom' ? 6 : 4 }}>
                <Select
                  label="X axis"
                  searchable
                  value={xAxis}
                  onChange={(v) => setXAxis(v || 'timestamp')}
                  data={axisOptions.x}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: rangePreset === 'custom' ? 6 : 4 }}>
                <Select
                  label="Y axis"
                  searchable
                  placeholder="Choose Y"
                  value={yAxis || null}
                  onChange={(v) => setYAxis(v || '')}
                  data={axisOptions.y}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: rangePreset === 'custom' ? 6 : 4 }}>
                <Button
                  variant="light"
                  color="pink"
                  leftSection={<IconRefresh size={16} />}
                  onClick={() => fetchData(range.start, range.end)}
                  disabled={!selectedUserId}
                  fullWidth
                >
                  Refresh
                </Button>
              </Grid.Col>
            </Grid>

            <Divider />

            <Card radius="md" withBorder p="md">
              <Group justify="space-between" align="flex-start" mb="sm">
                <div>
                  <Text fw={650}>Plot</Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    {yUnit ? `Unit: ${yUnit}` : ' '}
                  </Text>
                </div>
                <Badge variant="light">
                  {range.start} → {range.end}
                </Badge>
              </Group>
              {xAxis === 'timestamp' && yAxis && yAxis.startsWith('ts_') && rawTimeseriesRows.length > 0 ? (
                <ExplorerChart rows={rawTimeseriesRows} xKey="timestamp" yKey="value" yUnit={yUnit} timeLabelMode="datetime" />
              ) : xAxis.startsWith('ts_') && yAxis && yAxis.startsWith('ts_') && pairedTimeseriesRows.length > 0 ? (
                <ExplorerChart rows={pairedTimeseriesRows} xKey="xValue" yKey="yValue" yUnit={yUnit} timeLabelMode="date" />
              ) : (
                <ExplorerChart rows={dailyRows} xKey={xAxis} yKey={yAxis || 'workout_minutes'} yUnit={yUnit} timeLabelMode="date" />
              )}
            </Card>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}

export default FindCorrelations;
